import { isBefore, isSameDay } from 'date-fns';
import { useSortedGoals } from 'features/goals';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { Goal } from 'shared/types/goal';
import { InsightSections } from 'shared/types/insights';
import { sortItems } from 'shared/utils/sort-items';

export const useInsightsGoals = () => {
  const today = useToday();
  const user = useUser();
  const sortedGoals = useSortedGoals();

  return useMemo(() => {
    // Filter active main goals from sorted goals
    let sortedActiveGoals = sortedGoals.filter(
      ({ archivedAt, completedAt, parentIds }) =>
        !archivedAt && !completedAt && !parentIds?.length,
    );
    if (
      user?.settings?.insights?.sections?.[InsightSections.Goals]?.order?.length
    ) {
      sortedActiveGoals = sortItems(
        sortedActiveGoals,
        user.settings.insights.sections[InsightSections.Goals].order,
        'id',
      );
    }

    // Add missing active subGoals if enabled
    if (user?.settings?.insights?.showActiveSubGoals) {
      const processActiveGoalAndSubGoals = (goal: Goal) => {
        // Check if goal is active to process it correctly
        const goalIsActive =
          (user?.settings?.insights?.showHiddenItems ||
            !user?.settings?.insights?.sections?.[
              InsightSections.Goals
            ]?.hiddenItems.includes(goal.id)) &&
          !goal.archivedAt &&
          !goal.completedAt &&
          !!goal.startDate &&
          (isBefore(goal.startDate, today) || isSameDay(goal.startDate, today));
        const results = goalIsActive ? [goal] : [];

        // Get all subGoals from sortedGoals (they are already sorted correctly)
        const subGoals = sortedGoals.filter(
          ({ parentIds, archivedAt, completedAt }) =>
            !archivedAt &&
            !completedAt &&
            parentIds?.[parentIds.length - 1] === goal.id,
        );

        results.push(
          ...subGoals
            .map((subGoal) => processActiveGoalAndSubGoals(subGoal))
            .flat(1),
        );
        return results;
      };

      // Process all mainGoals
      return sortedActiveGoals.reduce<Goal[]>((acc, goal) => {
        acc.push(...processActiveGoalAndSubGoals(goal));
        return acc;
      }, []);
    }

    // When no subGoals have to be shown, filter out inactive goals
    return sortedActiveGoals.filter(
      ({ id, startDate }) =>
        (user?.settings?.insights?.showHiddenItems ||
          !user?.settings?.insights?.sections?.[
            InsightSections.Goals
          ]?.hiddenItems.includes(id)) &&
        !!startDate &&
        (isBefore(startDate, today) || isSameDay(startDate, today)),
    );
  }, [
    sortedGoals,
    today,
    user?.settings?.insights?.sections,
    user?.settings?.insights?.showActiveSubGoals,
    user?.settings?.insights?.showHiddenItems,
  ]);
};
